import React, {useEffect, useState} from "react";
import Dialog from "../../base/dialog";
import {DialogContent, DialogTitle} from "@mui/material";
import {Col, Row} from "reactstrap";
import Form from "../../base/form";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import Button from "../../base/button/button";
import useIsMounted from "../../../hooks/use-is-mounted";
import {makeRequired, makeValidate} from "mui-rff";
import * as Yup from "yup";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {deepEqual} from "../../../../core/services/utils/utils";
import Api from "../../../../core/services/api/api";
import Divider from "@mui/material/Divider";


const formKeys = {
    oldPassword: 'oldPassword',
    newPassword: 'newPassword',
    confirmNewPassword: 'confirmNewPassword'
}

const schema = Yup.object().shape({
    [formKeys.oldPassword]: Yup.string().nullable().required(ValidateMessages.required),
    [formKeys.newPassword]: Yup.string().nullable().required(ValidateMessages.required),
    [formKeys.confirmNewPassword]: Yup.string().oneOf([Yup.ref(formKeys.newPassword), null], "Passwords mush match").nullable().required(ValidateMessages.required),
});

const ChangeCurrentOperatorPasswordModal = ({open, setOpen}) => {
    const [loading, setLoading] = useState(false);
    const [initialValues, setInitialValues] = useState({});
    const isMounted = useIsMounted();
    const validate = makeValidate(schema);
    const required = makeRequired(schema);

    /**
     * With every open of the dialog, sets the initial values to empty object, thus resetting the form
     */
    useEffect(() => {
        setInitialValues({});
    }, [open])

    /**
     * Handles submitting the reset password information:
     * - Checks if the confirmed password is equal to entered password
     * - Calls the api regarding this action, if the api call was successful, sets the values as the initial values of the form
     * @param values
     */
    const onSubmit = async (values) => {
        setLoading(true);
        const response = await Api.resetCurrentOperatorPassword(values)
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setInitialValues(values ?? {});
        }
        setLoading(false);
    }

    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            maxWidth={'sm'}
            className={'change-current-operator-password-dialog'}
        >
            <DialogTitle>
                <Row xs={12} className='no-gutters justify-content-center w-100'>
                    <p className={`text-lg text-color-1 my-2 font-weight-semi-bold text-center`}>
                        Change Password
                    </p>
                </Row>
            </DialogTitle>
            <DialogContent dividers className={'pb-0 px-0'}>
                <div className='my-3 w-100'/>
                <Form
                    className='w-100'
                    onSubmit={onSubmit}
                    validate={validate}
                    initialValues={initialValues}
                    render={({values, form}) => {
                        return (
                            <Row className='no-gutters justify-content-around w-100'>
                                <Row className={'no-gutters w-100 justify-content-start px-4'}>
                                    <Col xs={12} className={'mb-3 mt-1'}>
                                        <OutlinedInputForm
                                            disabled={loading}
                                            label={'Old Password'}
                                            name={formKeys.oldPassword}
                                            type={'password'}
                                            required={required[formKeys.oldPassword]}
                                            value={values[formKeys.oldPassword] ?? ''}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                        />
                                    </Col>
                                    <Col xs={12} className={'mb-3'}>
                                        <OutlinedInputForm
                                            disabled={loading}
                                            label={'New Password'}
                                            name={formKeys.newPassword}
                                            type={'password'}
                                            required={required[formKeys.newPassword]}
                                            value={values[formKeys.newPassword] ?? ''}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                        />
                                    </Col>
                                    <Col xs={12} className={'mb-3'}>
                                        <OutlinedInputForm
                                            disabled={loading}
                                            label={'Confirm Password'}
                                            name={formKeys.confirmNewPassword}
                                            type={'password'}
                                            required={required[formKeys.confirmNewPassword]}
                                            value={values[formKeys.confirmNewPassword] ?? ''}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                        />
                                    </Col>
                                </Row>
                                <div className='my-1 w-100'/>
                                <Divider className='divider'/>
                                <Row className={'justify-content-between align-items-start w-100 p-0'}>
                                    <Button
                                        disabled={!open}
                                        variant={'dialog'}
                                        color={'primary'}
                                        onClick={() => setOpen(false)}
                                        borderRadius={'0 8px 0 8px'}
                                    >
                                        Close
                                    </Button>
                                    <Button
                                        disabled={!open || loading || deepEqual(initialValues, values)}
                                        variant={'dialog'}
                                        color={'primary'}
                                        type={'submit'}
                                        borderRadius={'8px 0 8px 0'}
                                    >
                                        {!loading ? 'Change Password' : 'Processing...'}
                                    </Button>
                                </Row>
                            </Row>
                        );
                    }}
                />
            </DialogContent>
        </Dialog>

    );
}

export default ChangeCurrentOperatorPasswordModal;
